import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EnhancedInput } from '@/components/ui/enhanced-input';
import { Label } from '@/components/ui/label';
import { LoadingCard } from '@/components/ui/loading';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { formatCurrency } from '@/lib/utils';
import { CreditCard, DollarSign, Loader2, Mail, Phone, MapPin, History, ChevronDown, ChevronUp, CreditCard as CreditCardIcon, Wallet, X } from 'lucide-react';
import type { Customer, Transaction, CompanySettings, PaymentMethod, Invoice } from '@/types/firebase';
import { db } from '@/lib/firebase';
import { collection, getDocs, query, where, orderBy, addDoc, limit, deleteDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '@/components/AuthProvider';
import { showNotification } from '@/lib/generalNotifications';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCompanySettings } from '@/lib/api';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PaymentErrorDialog } from '@/components/PaymentErrorDialog';
import { PaymentModal } from '@/components/PaymentModal';
import { showCardOnFileTransactionNotification } from '@/lib/notifications';
import { sendCardOnFileTransactionEmail } from '@/lib/emailNotifications';
import { isAchPaymentAvailable } from '@/utils/paymentGateways';

interface PaymentFormData {
  customerId: string;
  amount: string;
  description: string;
  salesTax?: number;
}

export default function VirtualTerminal() {
  const { user, loading } = useAuth();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [isCustomerSearchFocused, setIsCustomerSearchFocused] = useState(false);
  const [includePlatformFee, setIncludePlatformFee] = useState(false);
  const [includeSalesTax, setIncludeSalesTax] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'credit_card' | 'card_on_file'>('credit_card');
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [virtualInvoice, setVirtualInvoice] = useState<(Partial<Invoice> & { id: string }) | null>(null);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [formData, setFormData] = useState<PaymentFormData>({
    customerId: '',
    amount: '',
    description: '',
    salesTax: 0
  });
  const [noCustomers, setNoCustomers] = useState(false);
  const formDataRef = useRef(formData);
  const customersRef = useRef(customers);
  const includePlatformFeeRef = useRef(includePlatformFee);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [errorCode, setErrorCode] = useState('');

  // Helper function to get CSS class based on transaction status
  const getStatusClass = (status: string | undefined) => {
    if (!status) return 'bg-gray-100 text-gray-700';
    
    const statusStr = String(status).toLowerCase();
    if (statusStr === 'paid' || statusStr === 'completed') {
      return 'bg-green-100 text-green-700';
    } else if (statusStr === 'failed') {
      return 'bg-red-100 text-red-700';
    } else if (statusStr === 'refunded') {
      return 'bg-blue-100 text-blue-700';
    } else if (statusStr === 'overdue') {
      return 'bg-orange-100 text-orange-700';
    }
    return 'bg-gray-100 text-gray-700';
  };

  // Helper function to get display label based on transaction status
  const getStatusLabel = (status: string | undefined) => {
    if (!status) return 'Unknown';
    
    const statusStr = String(status).toLowerCase();
    if (statusStr === 'paid' || statusStr === 'completed') {
      return 'Paid';
    } else if (statusStr === 'failed') {
      return 'Failed';
    } else if (statusStr === 'refunded') {
      return 'Refunded';
    } else if (statusStr === 'overdue') {
      return 'Overdue';
    }
    return statusStr.charAt(0).toUpperCase() + statusStr.slice(1);
  };

  const { data: companySettings, error: settingsError } = useQuery<CompanySettings>({
    queryKey: ['companySettings', user?.companyId],
    queryFn: () => getCompanySettings(user?.companyId || `company_${user?.uid}`),
    enabled: !!user
  });

  const achAvailable = useMemo(() => isAchPaymentAvailable(companySettings), [companySettings]);

  const { data: recentTransactions = [] } = useQuery<Transaction[]>({
    queryKey: ['recentTransactions', formData.customerId],
    queryFn: async () => {
      if (!formData.customerId || !user) return [];
      
      // Ensure company ID is in the correct format
      const companyId = user.companyId?.startsWith('company_') 
        ? user.companyId 
        : `company_${user.uid}`;
      
      // Starting transaction query
      
      const transactionsRef = collection(db, 'transactions');
      
      try {
        // Try a simpler query first without orderBy to avoid index issues
        // Attempting simple query without orderBy
        const simpleQ = query(
          transactionsRef,
          where('customerId', '==', formData.customerId),
          where('companyId', '==', companyId)
        );
        
        const snapshot = await getDocs(simpleQ);
        // Simple query successful
        
        // Log the raw data for debugging
        // Check first transaction if exists
        
        // Sort the results in memory
        const transactions = snapshot.docs
          .map(doc => {
            const data = doc.data();
            // Ensure all required fields are present
            return {
              id: doc.id,
              ...data,
              // Handle different date formats
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : 
                         data.createdAt ? new Date(data.createdAt) : new Date(),
              // Ensure status is one of the expected values
              status: ['paid', 'failed', 'refunded', 'overdue'].includes(data.status) ? 
                     data.status : 'paid',
              // Ensure amount is a number
              amount: typeof data.amount === 'number' ? data.amount : 
                     parseFloat(data.amount) || 0
            } as Transaction;
          })
          .sort((a, b) => {
            const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
            const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
            return dateB.getTime() - dateA.getTime();
          })
          .slice(0, 10);
        
        // Transactions processed
        
        return transactions;
      } catch (error) {
        // Transaction query failed
        return [];
      }
    },
    enabled: !!formData.customerId && !!user,
    // Add refetch interval to periodically check for new transactions
    refetchInterval: 30000, // Refetch every 30 seconds
    // Force refetch when customer changes
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });

  // Add a new query to fetch customer spending statistics
  const { data: customerStats } = useQuery({
    queryKey: ['customerStats', formData.customerId],
    queryFn: async () => {
      if (!formData.customerId || !user) return null;
      
      const companyId = user.companyId?.startsWith('company_') 
        ? user.companyId 
        : `company_${user.uid}`;
      
      try {
        // Fetch all transactions for this customer
        const transactionsRef = collection(db, 'transactions');
        const q = query(
          transactionsRef,
          where('customerId', '==', formData.customerId),
          where('companyId', '==', companyId)
        );
        
        const snapshot = await getDocs(q);
        
        // Calculate totals
        let totalSpent = 0;
        let invoiceTotal = 0;
        let virtualTerminalTotal = 0;
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          // Only count successful transactions
          if (data.status === 'paid' || data.status === 'completed') {
            const amount = typeof data.amount === 'number' ? data.amount : parseFloat(data.amount) || 0;
            totalSpent += amount;
            
            // Check source to categorize the transaction
            // Invoice transactions have source='invoice' or have an invoiceId
            if (data.source === 'invoice' || data.invoiceId) {
              invoiceTotal += amount;
            } else {
              // All other paid transactions that aren't invoices count as virtual terminal
              virtualTerminalTotal += amount;
            }
          }
        });
        
        return {
          totalSpent,
          invoiceTotal,
          virtualTerminalTotal,
          transactionCount: snapshot.size
        };
      } catch (error) {
        // Error fetching customer statistics
        return null;
      }
    },
    enabled: !!formData.customerId && !!user
  });

  useEffect(() => {
    // Recent transactions updated
  }, [recentTransactions, formData.customerId]);

  useEffect(() => {
    if (settingsError) {
      // Error fetching company settings
      showNotification({
        title: "Error",
        message: "Failed to load company settings",
        type: "error"
      });
    }
  }, [settingsError]);

  useEffect(() => {
    // Auth state changed
  }, [user, loading]);

  useEffect(() => {
    // Company settings loaded
  }, [companySettings]);

  // Reset payment method to credit_card if ACH is disabled
  useEffect(() => {
    if (!achAvailable && paymentMethod === 'ach' as any) {
      setPaymentMethod('credit_card');
    }
  }, [achAvailable, paymentMethod]);

  const calculateTotalAmount = () => {
    const baseAmount = parseFloat(formData.amount) || 0;
    const taxAmount = includeSalesTax ? (formData.salesTax || 0) : 0;
    const subtotal = baseAmount + taxAmount;
    
    if (includePlatformFee && companySettings?.platformFee) {
      const platformFeeAmount = (subtotal * companySettings.platformFee) / 100;
      return subtotal + platformFeeAmount;
    }
    
    return subtotal;
  };

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    customersRef.current = customers;
    // Initialize filtered customers with all customers
    setFilteredCustomers(customers);
  }, [customers]);

  useEffect(() => {
    includePlatformFeeRef.current = includePlatformFee;
  }, [includePlatformFee]);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (loading) return;
      
      if (!user) {
        window.location.href = '/login';
        return;
      }

      try {
        const customersRef = collection(db, 'customers');
        const q = query(
          customersRef,
          where('companyId', '==', user.companyId || `company_${user.uid}`),
          where('status', '==', 'active')
        );
        
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
          // Set noCustomers state to true instead of redirecting
          setNoCustomers(true);
          setIsLoading(false);
          return;
        }

        // Reset noCustomers state if we have customers
        setNoCustomers(false);

        const customerData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Customer));
        
        // Sort customers: first by company name (if available), then by last name and first name
        const sortedCustomers = customerData.sort((a, b) => {
          // If both have company names, sort by company name
          if (a.companyName && b.companyName) {
            return a.companyName.localeCompare(b.companyName);
          }
          
          // If only a has company name, a comes first
          if (a.companyName && !b.companyName) {
            return -1;
          }
          
          // If only b has company name, b comes first
          if (!a.companyName && b.companyName) {
            return 1;
          }
          
          // If neither has company name, sort by last name, then first name
          const lastNameComparison = a.lastName.localeCompare(b.lastName);
          if (lastNameComparison !== 0) {
            return lastNameComparison;
          }
          
          return a.firstName.localeCompare(b.firstName);
        });
        
        setCustomers(sortedCustomers);
        
      } catch (error) {
        // Error loading customers
        if (error instanceof Error && error.message) {
          showNotification({
            title: "Error",
            message: error.message,
            type: "error"
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomers();
  }, [user, loading]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'amount') {
      const sanitizedValue = value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
      setFormData(prev => ({
        ...prev,
        amount: sanitizedValue
      }));

      // If sales tax is enabled, recalculate tax amount based on the new base amount
      if (includeSalesTax) {
        const baseAmount = parseFloat(sanitizedValue) || 0;
        const taxRate = (companySettings?.defaultTaxRate ?? 0) / 100;
        const calculatedTaxAmount = parseFloat((baseAmount * taxRate).toFixed(2));
        setFormData(prev => ({
          ...prev,
          amount: sanitizedValue,
          salesTax: calculatedTaxAmount
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAmountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value) {
      setFormData(prev => ({ ...prev, amount: '' }));
      return;
    }

    let formattedValue = value;
    
    if (!value.includes('.')) {
      formattedValue = `${value}.00`;
    } else {
      const [whole, decimal = ''] = value.split('.');
      formattedValue = `${whole}.${decimal.padEnd(2, '0')}`;
    }

    setFormData(prev => ({
      ...prev,
      amount: formattedValue
    }));
  };

  const handleCustomerChange = async (value: string) => {
    // Selected customer ID
    
    setFormData(prev => ({
      ...prev,
      customerId: value
    }));

    // Reset payment method selection when customer changes
    setPaymentMethod('credit_card');
    setSelectedPaymentMethod(null);
    
    // Fetch saved payment methods for this customer
    if (value) {
      setIsLoadingPaymentMethods(true);
      try {
        const paymentMethodsRef = collection(db, 'paymentMethods');
        const q = query(
          paymentMethodsRef,
          where('customerId', '==', value),
          where('companyId', '==', user?.companyId || `company_${user?.uid}`),
          where('type', '==', 'credit_card')
        );
        
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          let paymentMethodsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as PaymentMethod));
          
          // Filter by current default provider so tokens aren't mixed across gateways
          const defaultProvider = companySettings?.paymentGateways?.defaults?.card || 'nmi';
          paymentMethodsData = paymentMethodsData.filter((m: any) => {
            if (m.provider) {
              if (m.provider !== defaultProvider) return false;
              // Provider-specific validity checks
              if (m.provider === 'nmi') return !!m.nmiCustomerId;
              if (m.provider === 'stripe') return !!m.stripePaymentMethodId && !!m.stripeCustomerId;
              if (m.provider === 'payarc') return !!m.payarcPaymentToken || !!m.payarcCustomerRef;
              return false;
            }
            // Infer provider if missing (legacy docs)
            if (defaultProvider === 'nmi') return !!(m as any).nmiCustomerId;
            if (defaultProvider === 'stripe') return !!(m as any).stripePaymentMethodId && !!(m as any).stripeCustomerId;
            if (defaultProvider === 'payarc') return !!(m as any).payarcPaymentToken || !!(m as any).payarcCustomerRef;
            return false;
          });
          
          // Found saved payment methods
          setSavedPaymentMethods(paymentMethodsData);
          
          // Automatically select the first payment method (or the default one if available)
          const defaultMethod = paymentMethodsData.find(m => m.isDefault) || paymentMethodsData[0];
          setSelectedPaymentMethod(defaultMethod);
          
          // If there are saved payment methods, default to using card on file
          setPaymentMethod('card_on_file');
        } else {
          // No saved payment methods found for this customer
          setSavedPaymentMethods([]);
          
          // If there are no saved payment methods and ACH is not enabled, credit_card is the only option
          // We already set it above, so no need to do anything here
        }
      } catch (error) {
        // Error fetching saved payment methods
        setSavedPaymentMethods([]);
      } finally {
        setIsLoadingPaymentMethods(false);
      }
    } else {
      setSavedPaymentMethods([]);
    }
  };

  const handlePlatformFeeChange = (checked: boolean) => {
    setIncludePlatformFee(checked);
    includePlatformFeeRef.current = checked;
  };

  const handleSalesTaxChange = (checked: boolean) => {
    setIncludeSalesTax(checked);
    if (checked) {
      // Calculate tax amount based on the default tax rate from company settings
      const baseAmount = parseFloat(formData.amount) || 0;
      const taxRate = (companySettings?.defaultTaxRate ?? 0) / 100;
      const calculatedTaxAmount = parseFloat((baseAmount * taxRate).toFixed(2));
      setFormData(prev => ({ ...prev, salesTax: calculatedTaxAmount }));
    } else {
      setFormData(prev => ({ ...prev, salesTax: 0 }));
    }
  };

  const handlePayButtonClick = () => {
    // Validate required fields first
    if (!formData.customerId) {
      showNotification({
        title: "Missing Information",
        message: "Please select a customer before processing payment",
        type: "warning"
      });
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      showNotification({
        title: "Missing Information",
        message: "Please enter a valid payment amount",
        type: "warning"
      });
      return;
    }
    
    // If using card on file, validate that a card is selected
    if (paymentMethod === 'card_on_file') {
      if (!selectedPaymentMethod) {
        showNotification({
          title: "Missing Information",
          message: "Please select a card on file",
          type: "warning"
        });
        return;
      }
      
      // Process payment with saved card
      handlePaymentWithSavedMethod();
    } else {
      try {
        // Create a virtual invoice for the payment modal
        const newVirtualInvoice = createVirtualInvoice();
        
        setVirtualInvoice(newVirtualInvoice);
        // Set the payment modal to open
        setIsPaymentModalOpen(true);
      } catch (error) {
        // Error creating virtual invoice
        showNotification({
          title: "Error",
          message: "Failed to process payment. Please try again.",
          type: "error"
        });
      }
    }
  };

  const handlePaymentSuccess = async (transactionId: string) => {
    // Payment successful with transaction ID
    
    // Show a success notification
    showNotification({
      title: "Payment Successful",
      message: `Transaction ID: ${transactionId}`,
      type: "success"
    });
    
    // Reset form
    setFormData({
      customerId: '',
      amount: '',
      description: '',
      salesTax: 0
    });
    setPaymentMethod('credit_card');
    setSelectedPaymentMethod(null);
    setSavedPaymentMethods([]);
    
    // Refetch transactions to show the new payment (SPA behavior)
    queryClient.invalidateQueries({ queryKey: ['recentTransactions'] });
    queryClient.invalidateQueries({ queryKey: ['customerStats'] });
  };

  // Create a virtual invoice object from the form data
  const createVirtualInvoice = (): Partial<Invoice> & { id: string } => {
    const selectedCustomer = customers.find(c => c.id === formData.customerId);
    
    if (!selectedCustomer) {
      throw new Error('Customer not found');
    }
    
    // Only include payment methods that are enabled in company settings
    const paymentMethods: ('credit_card' | 'ach')[] = ['credit_card'];
    
    // Add ACH only if it's enabled in company settings
    if (achAvailable) {
      paymentMethods.push('ach');
    }
    
    return {
      id: `virtual-terminal-${Date.now()}`,
      invoiceNumber: `VT-${Date.now()}`,
      paymentDescription: formData.description?.trim() || '',
      customerId: selectedCustomer.id,
      customerName: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
      customerEmail: selectedCustomer.email,
      customerAddress: `${selectedCustomer.street}, ${selectedCustomer.city}, ${selectedCustomer.state} ${selectedCustomer.zip}`,
      street: selectedCustomer.street,
      city: selectedCustomer.city,
      state: selectedCustomer.state,
      zip: selectedCustomer.zip,
      companyId: user?.companyId || `company_${user?.uid}`,
      customerCompanyName: selectedCustomer.companyName,
      paymentMethods: paymentMethods,
      taxRate: includeSalesTax ? (companySettings?.defaultTaxRate ?? 0) : 0,
      taxAmount: includeSalesTax ? parseFloat((formData.salesTax || 0).toFixed(2)) : 0,
      subtotal: parseFloat(formData.amount) || 0,
      total: calculateTotalAmount()
    };
  };
  
  // Calculate platform fee amount
  const platformFeeAmount = includePlatformFee && (companySettings?.platformFee ?? 0) > 0 && (paymentMethod === 'credit_card' || paymentMethod === 'card_on_file')
    ? ((parseFloat(formData.amount) || 0) + (includeSalesTax ? (formData.salesTax || 0) : 0)) * ((companySettings?.platformFee ?? 0) / 100) 
    : 0;
  
  // Calculate total with fee
  const totalWithFee = (parseFloat(formData.amount) || 0) + (includeSalesTax ? (formData.salesTax || 0) : 0) + platformFeeAmount;

  // Handle payment with saved payment method
  const handlePaymentWithSavedMethod = async () => {
    if (!selectedPaymentMethod || !formData.customerId) return;
     
    setIsProcessing(true);
    
    try {
      const customer = customers.find(c => c.id === formData.customerId);
      if (!customer) {
        throw new Error('Customer not found');
      }
      
      // Use calculateTotalAmount() to get the total including tax and platform fee
      const totalAmount = calculateTotalAmount();
      
      // Get a description for the transaction
      const description = formData.description?.trim() || 'Virtual Terminal Payment';
      
      // Process payment with the saved payment method token
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
      
      // Get customer company name if available
      const customerCompanyName = customer.companyName || '';
      
      // Use unified payments/charge for saved method
      const unifiedResp = await fetch('/api/payments/charge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: user?.companyId || `company_${user?.uid}`,
          amount: totalAmount,
          currency: 'usd',
          source: 'saved',
          savedMethod: { id: selectedPaymentMethod.id, provider: (selectedPaymentMethod as any).provider || (companySettings?.paymentGateways?.defaults?.card || 'nmi') },
          orderId: `VT-${Date.now()}`,
          description,
          invoiceId: virtualInvoice?.id,
          customerId: customer.id,
          customerName: `${customer.firstName} ${customer.lastName}`,
          customerEmail: customer.email,
          customerCompanyName: customer.companyName || '',
          platformFeeEnabled: includePlatformFee,
          platformFeePercentage: (companySettings?.platformFee ?? 0),
          platformFeeAmount: platformFeeAmount,
          salesTax: includeSalesTax ? parseFloat((formData.salesTax || 0).toFixed(2)) : 0,
          kind: 'card',
          returnUrl: window.location.href,
        })
      });

      // Check if the response is OK before parsing JSON
      if (!unifiedResp.ok) {
        const errorResponse = await unifiedResp.json();
        // Check for specific error about payment method being unusable
        if (errorResponse.error && (errorResponse.error.includes('stored card can no longer be used') ||
            errorResponse.error.includes('PaymentMethod was previously used') ||
            errorResponse.code === 'payment_method_unexpected_state')) {
          // Remove the stale payment method from the list
          setSavedPaymentMethods(prev => prev.filter(pm => pm.id !== selectedPaymentMethod.id));
          setSelectedPaymentMethod(null);
          throw new Error('This saved card can no longer be used. Please add a new payment method.');
        }
        throw new Error(errorResponse.error || `Payment failed with status ${unifiedResp.status}`);
      }

      const paymentResponse = await unifiedResp.json();

      // Determine if the transaction was approved or declined
      const isApproved = !!paymentResponse.success;
      const transactionId = paymentResponse.transactionId || paymentResponse.transactionid || paymentResponse.transaction_id || 'N/A';
      
      if (isApproved) {
        // Transaction is recorded by the backend in /api/payments/charge
        // No need to create duplicate transaction here
      }
      
      // Send email notification if customer has an email
      if (customer.email) {
        try {
          await sendCardOnFileTransactionEmail({
            transactionId: transactionId,
            amount: totalAmount,
            status: isApproved ? 'approved' : 'declined',
            authCode: paymentResponse.authcode || paymentResponse.authCode,
            cardLast4: selectedPaymentMethod.last4,
            responseText: paymentResponse.responsetext,
            customerEmail: customer.email,
            customerName: `${customer.firstName} ${customer.lastName}`,
            companyId: user?.companyId || `company_${user?.uid}`
          });
        } catch (emailError) {
          // Error sending transaction email notification
          // Continue with the process even if email fails
        }
      }
      
      // Show the card on file transaction notification with acknowledgment required
      showCardOnFileTransactionNotification({
        transactionid: transactionId,
        amount: totalAmount,
        status: isApproved ? 'approved' : 'declined',
        authcode: paymentResponse.authcode,
        cardLast4: selectedPaymentMethod.last4,
        responsetext: paymentResponse.responsetext,
        additionalMessage: isApproved 
          ? `Payment processed successfully for ${customer.firstName} ${customer.lastName}.`
          : `The payment could not be processed. Please try a different payment method or contact support.`,
        onClose: () => {
          // Reset form after user acknowledges the notification
          if (isApproved) {
            setFormData({
              customerId: '',
              amount: '',
              description: '',
              salesTax: 0
            });
            setPaymentMethod('credit_card');
            setSelectedPaymentMethod(null);
            setSavedPaymentMethods([]);
            
            // Refetch transactions instead of reloading (SPA behavior)
            queryClient.invalidateQueries({ queryKey: ['recentTransactions'] });
            queryClient.invalidateQueries({ queryKey: ['customerStats'] });
          }
        }
      });
      
    } catch (error) {
      // Error processing payment with saved method
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';

      // Show error notification
      showCardOnFileTransactionNotification({
        transactionid: 'ERROR',
        amount: parseFloat(formData.amount) || 0,
        status: 'declined',
        cardLast4: selectedPaymentMethod?.last4 || '',
        responsetext: errorMessage,
        additionalMessage: errorMessage.includes('saved card can no longer be used')
          ? 'The saved payment method is no longer valid. Please add a new card and try again.'
          : 'There was a problem processing your payment. Please try again or use a different payment method.'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Filter customers based on search term
  const handleCustomerSearch = (searchTerm: string) => {
    setCustomerSearchTerm(searchTerm);
    
    if (!searchTerm.trim()) {
      // If search term is empty, show all customers
      setFilteredCustomers(customers);
      return;
    }
    
    const lowerCaseSearchTerm = searchTerm.toLowerCase().trim();
    
    // Filter customers based on company name, first name, or last name
    const filtered = customers.filter(customer => {
      const companyNameMatch = customer.companyName?.toLowerCase().includes(lowerCaseSearchTerm);
      const firstNameMatch = customer.firstName.toLowerCase().includes(lowerCaseSearchTerm);
      const lastNameMatch = customer.lastName.toLowerCase().includes(lowerCaseSearchTerm);
      const fullNameMatch = `${customer.firstName} ${customer.lastName}`.toLowerCase().includes(lowerCaseSearchTerm);
      
      return companyNameMatch || firstNameMatch || lastNameMatch || fullNameMatch;
    });
    
    setFilteredCustomers(filtered);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.location.href = '/app/transactions'}
          className="text-sm"
        >
          <History className="mr-2 h-4 w-4" />
          Transaction History
        </Button>
      </div>

      {/* No Customers UI */}
      {noCustomers ? (
        <Card className="p-8 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-5">You're almost ready to accept payments!</h2>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-6 shadow-sm border border-blue-100">
              <p className="text-lg text-gray-700 mb-4">
                Before you can use the Virtual Terminal, you'll need to add at least one customer to your account.
              </p>
              <p className="text-md text-gray-600 mb-6">
                The Virtual Terminal allows you to process payments manually for your customers over the phone or in person.
                Once you've added your first customer, you'll be able to process payments right away.
              </p>
              
              <Button 
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-6 py-2"
                onClick={() => window.location.href = '/app/customers'}
              >
                Add Your First Customer
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="text-blue-500 mb-2">
                  <CreditCard className="h-6 w-6" />
                </div>
                <h3 className="font-medium text-gray-800 mb-1">Accept Payments</h3>
                <p className="text-gray-600 text-sm">Process credit card and ACH payments securely.</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="text-blue-500 mb-2">
                  <DollarSign className="h-6 w-6" />
                </div>
                <h3 className="font-medium text-gray-800 mb-1">Track Transactions</h3>
                <p className="text-gray-600 text-sm">View payment history and customer spending stats.</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="text-blue-500 mb-2">
                  <Wallet className="h-6 w-6" />
                </div>
                <h3 className="font-medium text-gray-800 mb-1">Save Payment Methods</h3>
                <p className="text-gray-600 text-sm">Store customer payment details securely for future use.</p>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        // Main Card - Only show when there are customers
        <Card className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Payment Portal */}
            <div>
              <Card className="p-6 shadow-lg border-t-4 border-blue-500">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Payment Details</h2>
                </div>

                <form className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer" className="text-gray-700">Select Customer</Label>
                    
                    {formData.customerId && customers.find(c => c.id === formData.customerId) ? (
                      // Selected customer display - clickable to reopen search
                      <div 
                        className="p-2 rounded-md cursor-pointer hover:bg-gray-50 border border-gray-200 transition-colors flex items-center justify-between"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, customerId: '' }));
                          setCustomerSearchTerm('');
                          // Focus the search input after a short delay to allow state update
                          setTimeout(() => {
                            const searchInput = document.getElementById('customerSearch');
                            if (searchInput) {
                              searchInput.focus();
                            }
                          }, 50);
                        }}
                      >
                        <div className="font-medium text-gray-900">
                          {(() => {
                            const selectedCustomer = customers.find(c => c.id === formData.customerId);
                            return selectedCustomer ? 
                              (selectedCustomer.companyName ? 
                                `${selectedCustomer.companyName} (${selectedCustomer.firstName} ${selectedCustomer.lastName})` : 
                                `${selectedCustomer.firstName} ${selectedCustomer.lastName}`) : '';
                          })()}
                        </div>
                        <div className="flex items-center text-gray-500 text-sm">
                          <span className="mr-1">Change</span>
                          <ChevronDown className="h-4 w-4" />
                        </div>
                      </div>
                    ) : (
                      // Search input and dropdown
                      <div className="relative">
                        <EnhancedInput
                          id="customerSearch"
                          placeholder={isLoading ? "Loading customers..." : "Search customers..."}
                          value={customerSearchTerm}
                          onChange={(e) => handleCustomerSearch(e.target.value)}
                          onFocus={() => setIsCustomerSearchFocused(true)}
                          onBlur={() => {
                            // Auto-select if only one customer matches
                            if (filteredCustomers.length === 1 && customerSearchTerm.trim()) {
                              const customer = filteredCustomers[0];
                              handleCustomerChange(customer.id);
                              setCustomerSearchTerm('');
                            }
                            // Delay hiding the dropdown to allow for click events on the options
                            setTimeout(() => setIsCustomerSearchFocused(false), 200);
                          }}
                          className="w-full bg-white pr-10"
                          disabled={isLoading}
                          leftIcon={isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : undefined}
                        />
                        
                        {isLoading && (
                          <div className="absolute right-3 top-2.5">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        )}
                        
                        {!isLoading && customerSearchTerm && (
                          <button
                            type="button"
                            onClick={() => {
                              setCustomerSearchTerm('');
                              setIsCustomerSearchFocused(true);
                            }}
                            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                          >
                            <X className="h-4 w-4" />
                            <span className="sr-only">Clear search</span>
                          </button>
                        )}
                        
                        {(isCustomerSearchFocused || customerSearchTerm) && (
                          <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-60 overflow-auto">
                            {filteredCustomers.length === 0 ? (
                              <div className="p-2 text-center text-gray-500">
                                No customers found
                              </div>
                            ) : (
                              <div className="py-1">
                                {filteredCustomers.map((customer) => (
                                  <div
                                    key={customer.id}
                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                    onClick={() => {
                                      handleCustomerChange(customer.id);
                                      setCustomerSearchTerm('');
                                    }}
                                  >
                                    {customer.companyName ? 
                                      `${customer.companyName} (${customer.firstName} ${customer.lastName})` : 
                                      `${customer.firstName} ${customer.lastName}`}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="amount" className="text-gray-700">Payment Amount</Label>
                      <EnhancedInput
                        id="amount"
                        name="amount"
                        type="text"
                        placeholder="0.00"
                        value={formData.amount}
                        onChange={handleInputChange}
                        onBlur={handleAmountBlur}
                        leftIcon={<DollarSign className="h-5 w-5" />}
                        className="bg-white"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description" className="text-gray-700">Description (Optional)</Label>
                      <EnhancedInput
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Enter payment description"
                        className="bg-white"
                      />
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeSalesTax"
                          checked={includeSalesTax}
                          onCheckedChange={handleSalesTaxChange}
                        />
                        <Label htmlFor="includeSalesTax">Add Sales Tax ({companySettings?.defaultTaxRate || 0}%)</Label>
                      </div>
                      
                      {includeSalesTax && (
                        <div className="flex-1">
                          <EnhancedInput
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Enter tax amount"
                            value={formData.salesTax || ''}
                            onChange={(e) => setFormData(prev => ({ 
                              ...prev, 
                              salesTax: parseFloat(e.target.value) || 0 
                            }))}
                            className="max-w-[200px]"
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="includePlatformFee"
                        checked={includePlatformFee}
                        onCheckedChange={handlePlatformFeeChange}
                        disabled={!companySettings?.platformFee}
                      />
                      <Label htmlFor="includePlatformFee">
                        Add Platform Fee ({companySettings?.platformFee || 0}%)
                      </Label>
                    </div>

                    {formData.amount && (
                      <div className="text-sm bg-gray-50 p-4 rounded-md border border-gray-100">
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-600">Base Amount:</span>
                          <span className="font-medium">{formatCurrency(parseFloat(formData.amount) || 0)}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-600">Sales Tax ({(companySettings?.defaultTaxRate ?? 0)}%):</span>
                          <span className={`font-medium ${includeSalesTax ? "" : "text-gray-400"}`}>
                            {formatCurrency(includeSalesTax ? (formData.salesTax || 0) : 0)}
                          </span>
                        </div>
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-600">Platform Fee ({(companySettings?.platformFee ?? 0)}%):</span>
                          <span className={`font-medium ${includePlatformFee ? "" : "text-gray-400"}`}>
                            {formatCurrency(platformFeeAmount)}
                          </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-200">
                          <span className="font-semibold text-gray-700">Total Amount:</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(calculateTotalAmount())}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Payment Method Selection - Only show if there are multiple options */}
                  {(savedPaymentMethods.length > 0 || achAvailable) && 
                    <div className="grid md:grid-cols-2 gap-6 mt-4">
                      <Card 
                        className={`p-4 transition-shadow hover:shadow-lg cursor-pointer ${paymentMethod === 'credit_card' ? 'border-blue-500 border-2' : ''}`}
                        onClick={() => {
                          setPaymentMethod('credit_card');
                          setSelectedPaymentMethod(null); // Clear selected payment method when switching to new card
                        }}>
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="h-5 w-5 text-blue-500" />
                          <h3 className="text-base font-semibold">Use a New Credit Card</h3>
                        </div>
                      </Card>

                      {/* Card on File option - only show if customer has saved payment methods */}
                      {savedPaymentMethods.length > 0 && (
                        <Card 
                          className={`p-4 transition-shadow hover:shadow-lg cursor-pointer ${paymentMethod === 'card_on_file' ? 'border-purple-500 border-2' : ''}`}
                          onClick={() => setPaymentMethod('card_on_file')}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Wallet className="h-5 w-5 text-purple-500" />
                            <h3 className="text-base font-semibold">Card on File</h3>
                          </div>
                          
                          {selectedPaymentMethod && (
                            <div className="text-sm text-gray-600 mt-1">
                              {selectedPaymentMethod.cardType} •••• {selectedPaymentMethod.last4}
                              {selectedPaymentMethod.expiryMonth && selectedPaymentMethod.expiryYear ? 
                                ` (${selectedPaymentMethod.expiryMonth}/${selectedPaymentMethod.expiryYear})` : ''}
                            </div>
                          )}
                        </Card>
                      )}
                    </div>
                  }

                  <Button
                    type="button"
                    className="w-full py-6 text-base mt-4"
                    variant="success"
                    disabled={isProcessing || !formData.amount || !formData.customerId || (paymentMethod === 'card_on_file' && !selectedPaymentMethod)}
                    onClick={handlePayButtonClick}
                  >
                    {isProcessing ? (
                      <span className="flex items-center justify-center">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        {paymentMethod === 'credit_card' ? (
                          <>
                            <CreditCard className="mr-2 h-5 w-5" />
                            Pay {formatCurrency(calculateTotalAmount())}
                          </>
                        ) : (
                          <>
                            <Wallet className="mr-2 h-5 w-5" />
                            Charge {formatCurrency(calculateTotalAmount())} to card on file
                          </>
                        )}
                      </span>
                    )}
                  </Button>
                </form>
              </Card>
            </div>

            {/* Customer Information and Recent Transactions */}
            <div className="space-y-6">
              {formData.customerId ? (
                <>
                  <Card className="shadow-sm border-t-4 border-green-500">
                    {customers.map((customer) => customer.id === formData.customerId && (
                      <div key={customer.id} className="divide-y divide-gray-100">
                        {/* Header with customer name and status */}
                        <div className="p-4 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold">
                              {customer.firstName.charAt(0)}{customer.lastName.charAt(0)}
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {customer.companyName || `${customer.firstName} ${customer.lastName}`}
                              </h3>
                              {savedPaymentMethods.length > 0 && (
                                <div className="flex items-center space-x-2 mt-0.5">
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                                    Card on File
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Customer spending statistics */}
                        <div className="p-4">
                          <div className="flex items-center text-gray-500 mb-2 text-sm">
                            <DollarSign className="h-3.5 w-3.5 mr-1.5" />
                            <span className="font-medium">Spending Statistics</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div className="bg-gray-50 p-2 rounded-md">
                              <div className="text-xs text-gray-500">Total Spent</div>
                              <div className="font-semibold text-gray-900">
                                {customerStats ? formatCurrency(customerStats.totalSpent) : '—'}
                              </div>
                            </div>
                            <div className="bg-gray-50 p-2 rounded-md">
                              <div className="text-xs text-gray-500">Invoice Payments</div>
                              <div className="font-semibold text-gray-900">
                                {customerStats ? formatCurrency(customerStats.invoiceTotal) : '—'}
                              </div>
                            </div>
                            <div className="bg-gray-50 p-2 rounded-md">
                              <div className="text-xs text-gray-500">Virtual Terminal</div>
                              <div className="font-semibold text-gray-900">
                                {customerStats ? formatCurrency(customerStats.virtualTerminalTotal) : '—'}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Contact info in compact grid */}
                        <div className="p-4 grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="flex items-center text-gray-500 mb-1">
                              <Mail className="h-3.5 w-3.5 mr-1.5" />
                              <span className="font-medium">Email</span>
                            </div>
                            <p className="text-gray-900 truncate">{customer.email}</p>
                          </div>
                          <div>
                            <div className="flex items-center text-gray-500 mb-1">
                              <Phone className="h-3.5 w-3.5 mr-1.5" />
                              <span className="font-medium">Phone</span>
                            </div>
                            <p className="text-gray-900">{customer.phone}</p>
                          </div>
                        </div>
                        
                        {/* Address in compact format */}
                        <div className="p-4">
                          <div className="flex items-center text-gray-500 mb-1 text-sm">
                            <MapPin className="h-3.5 w-3.5 mr-1.5" />
                            <span className="font-medium">Address</span>
                          </div>
                          <p className="text-sm text-gray-900">{customer.street}</p>
                          <p className="text-sm text-gray-900">
                            {customer.city}, {customer.state} {customer.zip}
                          </p>
                        </div>
                      </div>
                    ))}
                  </Card>

                  {/* Recent transactions section */}
                  <Card className="shadow-sm border-t-4 border-purple-500">
                    <div className="p-4 flex items-center justify-between border-b border-gray-100">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <History className="h-4 w-4 mr-2 text-purple-500" />
                        Recent Transactions
                      </h3>
                      {formData.customerId && recentTransactions.length > 0 && (
                        <span className="text-xs font-medium text-gray-500">
                          Last {recentTransactions.length} transactions
                        </span>
                      )}
                    </div>

                    <div className="divide-y divide-gray-100">
                      {formData.customerId && recentTransactions && recentTransactions.length > 0 ? (
                        recentTransactions.map((transaction) => (
                          <div key={transaction.id} className="p-3 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2 flex-1 overflow-hidden">
                                {/* Date */}
                                <span className="text-xs text-gray-500 whitespace-nowrap min-w-[80px]">
                                  {(() => {
                                    try {
                                      const date = transaction.createdAt instanceof Date 
                                        ? transaction.createdAt 
                                        : transaction.date
                                          ? new Date(transaction.date)
                                          : new Date(transaction.createdAt);
                                      return date.toLocaleDateString();
                                    } catch (e) {
                                      return 'Unknown date';
                                    }
                                  })()}
                                </span>
                                
                                {/* Description */}
                                <span className="text-xs text-gray-700 truncate flex-1">
                                  {transaction.description || 'Payment'}
                                </span>
                              </div>
                              
                              <div className="flex items-center space-x-2 ml-2">
                                {/* Card Last 4 */}
                                {transaction.cardLast4 && (
                                  <div className="text-xs text-gray-500 flex items-center whitespace-nowrap">
                                    <CreditCard className="h-3 w-3 mr-1 text-gray-400" />
                                    {transaction.cardLast4}
                                  </div>
                                )}
                                
                                {/* Status */}
                                <StatusIndicator
                                  status={transaction.status as any}
                                  size="sm"
                                />
                                
                                {/* Amount */}
                                <span className="font-medium text-gray-900 whitespace-nowrap min-w-[80px] text-right">
                                  {formatCurrency(transaction.amount || 0)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-6 text-center text-gray-500">
                          {formData.customerId 
                            ? "No recent transactions found for this customer"
                            : "Select a customer to view their recent transactions"}
                        </div>
                      )}
                    </div>
                  </Card>
                </>
              ) : (
                <Card className="p-6 shadow-lg border-t-4 border-blue-500 text-center">
                  <div className="text-lg font-semibold text-gray-900 mb-2">Select a Customer</div>
                  <p className="text-gray-600">
                    Choose a customer from the dropdown to process a payment or view transaction history.
                  </p>
                </Card>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Payment Modal */}
      {formData.customerId && virtualInvoice && (
        <PaymentModal
          open={isPaymentModalOpen}
          onOpenChange={(open) => { 
            if (!open) setVirtualInvoice(null); 
            setIsPaymentModalOpen(open); 
          }}
          invoice={virtualInvoice as Partial<Invoice> & { id: string }}
          invoiceAmount={parseFloat(formData.amount) || 0}
          platformFee={(companySettings as CompanySettings | null)?.platformFee ?? 0}
          enablePlatformFee={includePlatformFee}
          onPaymentSuccess={handlePaymentSuccess}
          salesTax={includeSalesTax ? parseFloat((formData.salesTax || 0).toFixed(2)) : 0}
          enableSalesTax={includeSalesTax}
        />
      )}

      <PaymentErrorDialog 
        open={isErrorDialogOpen}
        onOpenChange={setIsErrorDialogOpen}
        errorCode={errorCode}
      />
    </div>
  );
}
